import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, compressImage, downloadFile, formatApiErrorDetail } from "@/lib/api";
import {
  PLAN_STATUSES, PLAN_STATUS_LABELS, PLAN_STATUS_COLORS, PLAN_ORIGIN_LABELS,
  PRIORITY_LABELS, PRIORITY_COLORS, STATUS_LABELS, STATUS_COLORS,
  ACTION_TYPE_LABELS, ACTION_STATUS_LABELS, EFFECTIVENESS_LABELS, EFFECTIVENESS_COLORS,
  ROOT_CAUSE_METHODS, formatDate,
} from "@/lib/labels";
import { Button } from "@/components/ui/button";
import RecordActions from "@/components/RecordActions";
import ConfirmDeleteDialog from "@/components/ConfirmDeleteDialog";
import ReviewDialog from "@/components/ReviewDialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EvidenceGallery, BeforeAfterSlider } from "@/components/EvidenceGallery";
import {
  ArrowLeft, Zap, AlertTriangle, History, Plus, Trash2, Camera, Sparkles,
  ClipboardList, Save, Target, ListChecks, Pencil, Columns2, FileDown,
} from "lucide-react";


function ActionDialog({
  open,
  onOpenChange,
  planId,
  initial,
  onSaved,
  canCreate,
  canEdit,
}) {
  const [form, setForm] = useState({ title: "", description: "", type: "corretiva", responsible_name: "", deadline: "", priority: "media" });
  useEffect(() => {
    if (open) setForm(initial ? { ...initial, deadline: initial.deadline?.slice(0, 10) || "" }
      : { title: "", description: "", type: "corretiva", responsible_name: "", deadline: "", priority: "media" });
  }, [open, initial]);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const save = async () => {
    const allowed = initial?.id ? canEdit : canCreate;

    if (!allowed) {
      return toast.error(
        initial?.id
          ? "Você não possui permissão para editar ações."
          : "Você não possui permissão para criar ações.",
      );
    }

    if (form.title.trim().length < 2) {
      return toast.error("Informe o título da ação");
    }
    const body = { ...form, deadline: form.deadline ? `${form.deadline}T23:59:59` : null };
    delete body.id; delete body.subtasks; delete body.status; delete body.created_by; delete body.created_at;
    try {
      if (initial?.id) await api.put(`/action-plans/${planId}/actions/${initial.id}`, body);
      else await api.post(`/action-plans/${planId}/actions`, body);
      toast.success(initial?.id ? "Ação atualizada" : "Ação adicionada");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="action-dialog">
        <DialogHeader><DialogTitle className="font-heading">{initial?.id ? "Editar Ação" : "Nova Ação"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="Título da ação *" data-testid="action-title-input" />
          <Textarea rows={2} value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="Descrição" className="resize-none" data-testid="action-description-input" />
          <div className="grid grid-cols-3 gap-3">
            <Select value={form.type} onValueChange={(v) => set({ type: v })}>
              <SelectTrigger className="h-9" data-testid="action-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(ACTION_TYPE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={form.priority} onValueChange={(v) => set({ priority: v })}>
              <SelectTrigger className="h-9" data-testid="action-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(PRIORITY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="date" value={form.deadline} onChange={(e) => set({ deadline: e.target.value })} className="h-9" data-testid="action-deadline-input" />
          </div>
          <Input value={form.responsible_name} onChange={(e) => set({ responsible_name: e.target.value })} placeholder="Responsável pela ação" data-testid="action-responsible-input" />
          <Button
            className="w-full"
            onClick={save}
            disabled={
              initial?.id
                ? !canEdit
                : !canCreate
            }
            data-testid="action-save-btn"
          >
            Salvar Ação
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ActionPlanDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    user,
    can,
    isGlobal,
    scope,
  } = useAuth();

  const canView = can("planos", "view");
  const canCreate = can("planos", "create");
  const canEdit = can("planos", "edit");
  const canExecute = can("planos", "execute");
  const canReview = can("planos", "review");
  const canDelete = can("planos", "delete");
  const [plan, setPlan] = useState(null);
  const [actionDialog, setActionDialog] = useState(false);
  const [editingAction, setEditingAction] = useState(null);
  const [subtaskInputs, setSubtaskInputs] = useState({});
  const [rootCause, setRootCause] = useState({ method: "livre", text: "" });
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const evidenceRef = useRef(null);

  const exportPdf = async () => {
    setPdfLoading(true);
    try {
      await downloadFile(`/reports/action-plan/${id}/pdf`, `plano-${plan?.number || id}.pdf`);
      toast.success("PDF do plano gerado");
    } catch {
      toast.error("Falha ao gerar o PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  const load = () => api.get(`/action-plans/${id}`).then(({ data }) => {
    setPlan(data);
    setRootCause({ method: data.root_cause?.method || "livre", text: data.root_cause?.text || "" });
  }).catch(() => navigate("/planos-de-acao"));
  useEffect(() => {
    if (!canView) {
      return;
    }

    load();
  }, [id, canView]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!canView) {
    return (
      <div
        className="max-w-3xl mx-auto mt-10"
        data-testid="action-plan-access-denied"
      >
        <Card className="p-6 border-destructive/40 bg-destructive/5">
          <p className="font-semibold">
            Acesso ao Plano de Ação não autorizado.
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            A visibilidade e as ações deste módulo são controladas pela autorização central.
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => navigate("/planos-de-acao")}
          >
            Voltar para Planos de Ação
          </Button>
        </Card>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  const changeStatus = async (status) => {
    if (!canEdit) {
      return toast.error("Você não possui permissão para alterar o status.");
    }

    try {
      await api.patch(`/action-plans/${id}/status`, { status });
      toast.success(`Status alterado para ${PLAN_STATUS_LABELS[status]}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const saveRootCause = async () => {
    if (!canEdit) {
      return toast.error("Você não possui permissão para editar a causa raiz.");
    }

    try {
      await api.put(`/action-plans/${id}/root-cause`, { ...rootCause, photos: plan.root_cause?.photos || [] });
      toast.success("Causa raiz salva");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const actionStatus = async (actionId, status) => {
    if (!canExecute) {
      return toast.error("Você não possui permissão para executar ações.");
    }

    try {
      await api.patch(`/action-plans/${id}/actions/${actionId}/status`, { status });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const addSubtask = async (actionId) => {
    if (!canCreate) {
      return toast.error("Você não possui permissão para criar subtarefas.");
    }

    const desc = (subtaskInputs[actionId] || "").trim();
    if (desc.length < 2) return;
    await api.post(`/action-plans/${id}/actions/${actionId}/subtasks`, { description: desc });
    setSubtaskInputs((s) => ({ ...s, [actionId]: "" }));
    load();
  };

  const toggleSubtask = async (actionId, subId) => {
    await api.patch(`/action-plans/${id}/actions/${actionId}/subtasks/${subId}`);
    load();
  };

  const addEvidence = async (files) => {
    if (!canExecute) {
      return toast.error("Você não possui permissão para anexar evidências.");
    }

    const f = files?.[0];
    if (!f) return;
    const photo = await compressImage(f);
    await api.post(`/action-plans/${id}/evidences`, { photo, description: "" });
    toast.success("Evidência anexada");
    load();
  };

  const generateNarrative = async () => {
    if (!canExecute) {
      return toast.error("Você não possui permissão para gerar a narrativa IA.");
    }

    setNarrativeLoading(true);
    try {
      await api.post(`/action-plans/${id}/ai-narrative`);
      toast.success("Narrativa Inteligente gerada");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setNarrativeLoading(false);
    }
  };

  const nar = plan.ai_narrative;
  // Comparativo antes × depois: a evidência mais antiga contra a mais
  // recente (só com duas ou mais fotos). Antes estas variáveis eram usadas
  // sem definição e a aba Evidências derrubava a página.
  const orderedEvidences = [...(plan.evidences || [])]
    .filter((ev) => ev && ev.photo)
    .sort((a, b) => String(a.timestamp || "").localeCompare(String(b.timestamp || "")));
  const showCompare = orderedEvidences.length >= 2;
  const beforeEv = orderedEvidences[0] || {};
  const afterEv = orderedEvidences[orderedEvidences.length - 1] || {};
  const overdue = plan.overdue;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="action-plan-detail-page">
      <button onClick={() => navigate("/planos-de-acao")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="plan-back-btn">
        <ArrowLeft size={15} /> Planos de Ação
      </button>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="font-heading text-2xl font-bold tracking-tight font-mono" data-testid="plan-number">{plan.number}</h1>
            <Badge variant="outline" className={PLAN_STATUS_COLORS[plan.status]} data-testid="plan-status-badge">{PLAN_STATUS_LABELS[plan.status]}</Badge>
            <Badge variant="outline" className={PRIORITY_COLORS[plan.priority]}>{PRIORITY_LABELS[plan.priority]}</Badge>
            <Badge variant="outline">{PLAN_ORIGIN_LABELS[plan.origin] || plan.origin}</Badge>
            {plan.auto_generated && (
              <Badge variant="outline" className="bg-amber-500/15 text-amber-400 border-amber-500/30" data-testid="plan-auto-badge">
                <Zap size={11} className="mr-1" /> Automático
              </Badge>
            )}
            {plan.effectiveness?.result && (
              <Badge variant="outline" className={EFFECTIVENESS_COLORS[plan.effectiveness.result]} data-testid="plan-effectiveness-badge">
                {EFFECTIVENESS_LABELS[plan.effectiveness.result]}
              </Badge>
            )}
          </div>
          <p className="font-medium mt-1.5" data-testid="plan-title">{plan.title}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {plan.client_name} › {plan.unit_name} › {plan.area_name}{plan.subarea_name !== "—" ? ` › ${plan.subarea_name}` : ""} · {plan.category}
          </p>
          {!isGlobal && (
            <p className="text-[11px] text-muted-foreground mt-1">
              Escopo efetivo aplicado pela autorização central.
            </p>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <RecordActions
            module="planos"
            testId="plan-detail"
            onReview={
              canReview
                ? () => setReviewOpen(true)
                : undefined
            }
            onDelete={
              canDelete
                ? () => setDeleteOpen(true)
                : undefined
            }
          />
          <Button variant="outline" onClick={exportPdf} disabled={pdfLoading} data-testid="plan-pdf-btn">
            <FileDown size={15} className="mr-2" /> {pdfLoading ? "Gerando..." : "PDF"}
          </Button>
          {canEdit && (
            <Select value={plan.status} onValueChange={changeStatus}>
              <SelectTrigger className="w-full sm:w-52" data-testid="plan-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PLAN_STATUSES.map((s) => <SelectItem key={s} value={s}>{PLAN_STATUS_LABELS[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="px-4 py-3 border-border bg-card">
          <p className="text-xs text-muted-foreground">Responsável</p>
          <p className="font-medium text-sm mt-0.5 truncate" data-testid="plan-responsible">{plan.responsible_name || "Não definido"}</p>
        </Card>
        <Card className="px-4 py-3 border-border bg-card">
          <p className="text-xs text-muted-foreground">Prazo</p>
          <p className={`font-medium text-sm mt-0.5 ${overdue ? "text-red-400 flex items-center gap-1" : ""}`} data-testid="plan-deadline">
            {overdue && <AlertTriangle size={12} />}{formatDate(plan.deadline)}
          </p>
        </Card>
        <Card className="px-4 py-3 border-border bg-card">
          <p className="text-xs text-muted-foreground">Criado</p>
          <p className="font-medium text-sm mt-0.5">{formatDate(plan.created_at)}</p>
          <p className="text-[10px] text-muted-foreground">{plan.created_by_name}</p>
        </Card>
        <Card className="px-4 py-3 border-border bg-card" data-testid="plan-progress-card">
          <p className="text-xs text-muted-foreground">Execução — {plan.progress?.done || 0}/{plan.progress?.total || 0} itens</p>
          <div className="flex items-center gap-2 mt-2">
            <div className="h-2 flex-1 rounded-full bg-muted overflow-hidden">
              <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${plan.progress?.pct || 0}%` }} />
            </div>
            <span className="text-sm font-semibold">{plan.progress?.pct || 0}%</span>
          </div>
        </Card>
      </div>

      {plan.effectiveness?.result && (
        <Card className={`p-4 border ${plan.effectiveness.result === "efetivo" ? "border-emerald-500/40 bg-emerald-500/5" : plan.effectiveness.result === "parcial" ? "border-amber-500/40 bg-amber-500/5" : "border-red-500/40 bg-red-500/5"}`} data-testid="plan-effectiveness-card">
          <p className="text-sm flex items-center gap-2">
            <Target size={15} />
            Efetividade avaliada em {formatDate(plan.effectiveness.evaluated_at)}: <strong>{EFFECTIVENESS_LABELS[plan.effectiveness.result]}</strong>
            <span className="text-muted-foreground">— {plan.effectiveness.new_occurrences} nova(s) ocorrência(s) no local em {plan.effectiveness.evaluate_after_days} dias</span>
          </p>
        </Card>
      )}

      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="rootcause" data-testid="tab-rootcause">Causa Raiz</TabsTrigger>
          <TabsTrigger value="actions" data-testid="tab-actions">Ações ({plan.actions?.length || 0})</TabsTrigger>
          <TabsTrigger value="evidences" data-testid="tab-evidences">Evidências ({plan.evidences?.length || 0})</TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai"><Sparkles size={13} className="mr-1" /> Narrativa IA</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4 mt-4">
          {(plan.auto_reason || plan.description) && (
            <Card className="p-4 border-border bg-card">
              <h3 className="font-heading font-semibold text-sm mb-1.5">Motivo / Objetivo</h3>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap" data-testid="plan-reason">{plan.auto_reason || plan.description}</p>
            </Card>
          )}
          <Card className="p-4 border-border bg-card">
            <h3 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2"><ClipboardList size={15} /> Ocorrências relacionadas ({plan.occurrences?.length || 0})</h3>
            {(plan.occurrences || []).length === 0 && <p className="text-sm text-muted-foreground">Nenhuma ocorrência vinculada.</p>}
            <div className="space-y-2">
              {(plan.occurrences || []).map((o) => (
                <button key={o.id} onClick={() => navigate(`/ocorrencias/${o.id}`)}
                  className="w-full text-left rounded-md border border-border px-3.5 py-2.5 hover:border-primary/50 transition-colors flex items-center gap-3"
                  data-testid={`plan-occurrence-${o.protocol}`}>
                  <span className="font-mono text-xs font-semibold text-primary shrink-0">{o.protocol}</span>
                  <span className="text-xs text-muted-foreground truncate flex-1">{o.description}</span>
                  <Badge variant="outline" className={`${STATUS_COLORS[o.status]} shrink-0`}>{STATUS_LABELS[o.status]}</Badge>
                </button>
              ))}
            </div>
          </Card>
          <Card className="p-4 border-border bg-card">
            <h3 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2"><History size={15} /> Timeline</h3>
            <div className="space-y-0 max-h-96 overflow-y-auto scrollbar-thin">
              {(plan.history || []).slice().reverse().map((h, i) => (
                <div key={i} className="flex gap-3 pb-4 last:pb-0" data-testid="plan-history-entry">
                  <div className="flex flex-col items-center">
                    <div className="h-2.5 w-2.5 rounded-full bg-primary mt-1 shrink-0" />
                    {i < plan.history.length - 1 && <div className="w-px flex-1 bg-border mt-1" />}
                  </div>
                  <div className="pb-1">
                    <p className="text-sm">{h.detail}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{h.user} · {formatDate(h.timestamp)}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="rootcause" className="mt-4">
          <Card className="p-5 border-border bg-card space-y-4" data-testid="rootcause-card">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h3 className="font-heading font-semibold text-sm">Análise de Causa Raiz</h3>
              {canEdit && (
                <Select value={rootCause.method} onValueChange={(v) => setRootCause((r) => ({ ...r, method: v }))}>
                  <SelectTrigger className="w-52 h-8 text-xs" data-testid="rootcause-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROOT_CAUSE_METHODS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <Textarea rows={8} value={rootCause.text} onChange={(e) => setRootCause((r) => ({ ...r, text: e.target.value }))}
              disabled={!canEdit}
              placeholder={rootCause.method === "5porques"
                ? "1º Porquê: ...\n2º Porquê: ...\n3º Porquê: ...\n4º Porquê: ...\n5º Porquê (causa raiz): ..."
                : "Descreva a análise de causa raiz..."}
              className="resize-none" data-testid="rootcause-text-input" />
            {canEdit && (
              <Button onClick={saveRootCause} data-testid="rootcause-save-btn"><Save size={14} className="mr-2" /> Salvar Causa Raiz</Button>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="actions" className="space-y-3 mt-4">
          {canEdit && (
            <Button variant="outline" size="sm" onClick={() => { setEditingAction(null); setActionDialog(true); }} data-testid="add-action-btn">
              <Plus size={14} className="mr-1.5" /> Adicionar Ação
            </Button>
          )}
          {(plan.actions || []).length === 0 && (
            <Card className="p-8 border-dashed border-border bg-card text-center text-sm text-muted-foreground">
              Nenhuma ação cadastrada. Adicione ações corretivas e preventivas ao plano.
            </Card>
          )}
          {(plan.actions || []).map((a) => (
            <Card key={a.id} className="p-4 border-border bg-card" data-testid={`plan-action-${a.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm">{a.title}</p>
                    <Badge variant="outline" className="text-[10px]">{ACTION_TYPE_LABELS[a.type] || a.type}</Badge>
                    <Badge variant="outline" className={`text-[10px] ${PRIORITY_COLORS[a.priority]}`}>{PRIORITY_LABELS[a.priority]}</Badge>
                  </div>
                  {a.description && <p className="text-xs text-muted-foreground mt-1">{a.description}</p>}
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {a.responsible_name ? `Responsável: ${a.responsible_name}` : ""}{a.deadline ? ` · Prazo: ${formatDate(a.deadline).split(" ")[0]}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {canExecute && (
                    <Select value={a.status} onValueChange={(v) => actionStatus(a.id, v)}>
                      <SelectTrigger className="h-8 w-36 text-xs" data-testid={`action-status-select-${a.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(ACTION_STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  {!canExecute && <Badge variant="outline">{ACTION_STATUS_LABELS[a.status]}</Badge>}
                  {canEdit && (
                    <>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingAction(a); setActionDialog(true); }} data-testid={`action-edit-btn-${a.id}`}>
                        <Pencil size={13} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={async () => { await api.delete(`/action-plans/${id}/actions/${a.id}`); load(); }} data-testid={`action-delete-btn-${a.id}`}>
                        <Trash2 size={13} />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <div className="mt-3 pl-1 space-y-1.5">
                {(a.subtasks || []).map((s) => (
                  <label key={s.id} className="flex items-center gap-2.5 text-sm cursor-pointer group" data-testid={`subtask-${s.id}`}>
                    <Checkbox checked={!!s.done} onCheckedChange={() => canExecute && toggleSubtask(a.id, s.id)} disabled={!canExecute} data-testid={`subtask-check-${s.id}`} />
                    <span className={s.done ? "line-through text-muted-foreground" : ""}>{s.description}</span>
                    {s.done && s.done_by && <span className="text-[10px] text-muted-foreground">({s.done_by})</span>}
                  </label>
                ))}
                {canExecute && (
                  <div className="flex gap-2 pt-1">
                    <Input value={subtaskInputs[a.id] || ""} onChange={(e) => setSubtaskInputs((s) => ({ ...s, [a.id]: e.target.value }))}
                      onKeyDown={(e) => e.key === "Enter" && addSubtask(a.id)}
                      placeholder="Nova subtarefa do checklist..." className="h-8 text-xs" data-testid={`subtask-input-${a.id}`} />
                    <Button variant="outline" size="sm" className="h-8" onClick={() => addSubtask(a.id)} data-testid={`subtask-add-btn-${a.id}`}>
                      <ListChecks size={13} />
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="evidences" className="mt-4">
          <Card className="p-5 border-border bg-card space-y-4" data-testid="evidences-card">
            {canExecute && (
              <>
                <Button variant="outline" size="sm" onClick={() => evidenceRef.current?.click()} data-testid="add-evidence-btn">
                  <Camera size={14} className="mr-1.5" /> Anexar Evidência
                </Button>
                <input ref={evidenceRef} type="file" accept="image/*" hidden onChange={(e) => addEvidence(e.target.files)} data-testid="evidence-file-input" />
              </>
            )}
            {(plan.evidences || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">Nenhuma evidência anexada.</p>
            ) : (
              <>
                <EvidenceGallery
                  photos={(plan.evidences || []).map((ev) => ev.photo)}
                  captions={(plan.evidences || []).map((ev) => `${ev.description || "Evidência"} — ${ev.user} · ${formatDate(ev.timestamp).split(" ")[0]}`)}
                  testId="plan-evidence-gallery"
                />
                {showCompare && (
                  <div className="pt-2">
                    <h4 className="font-heading font-semibold text-sm mb-1 flex items-center gap-2"><Columns2 size={15} className="text-primary" /> Comparativo Antes vs Depois</h4>
                    <p className="text-xs text-muted-foreground mb-3">Arraste o divisor para comparar as evidências.</p>
                    <BeforeAfterSlider
                      before={beforeEv.photo}
                      after={afterEv.photo}
                      beforeLabel={beforeEv.description || "Antes"}
                      afterLabel={afterEv.description || "Depois"}
                      testId="plan-before-after-slider"
                    />
                  </div>
                )}
              </>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="ai" className="mt-4">
          <Card className="p-5 border-border bg-card space-y-4" data-testid="ai-narrative-card">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h3 className="font-heading font-semibold text-sm flex items-center gap-2"><Sparkles size={15} className="text-primary" /> Narrativa Inteligente</h3>
              {canEdit && (
                <Button size="sm" onClick={generateNarrative} disabled={narrativeLoading} data-testid="generate-narrative-btn">
                  <Sparkles size={13} className="mr-1.5" /> {narrativeLoading ? "Gerando..." : nar ? "Regerar com IA" : "Gerar com IA"}
                </Button>
              )}
            </div>
            {!nar && !narrativeLoading && (
              <p className="text-sm text-muted-foreground">
                A IA gera automaticamente diagnóstico técnico, causas prováveis, ações corretivas/preventivas, frequência de monitoramento recomendada e recomendações técnicas com base nas ocorrências vinculadas.
              </p>
            )}
            {nar && (
              <div className="space-y-4 text-sm" data-testid="ai-narrative-content">
                {nar.resumo_executivo && (
                  <div className="rounded-md border border-primary/30 bg-primary/5 px-4 py-3">
                    <p className="text-xs text-muted-foreground mb-1">Resumo Executivo</p>
                    <p>{nar.resumo_executivo}</p>
                  </div>
                )}
                {nar.diagnostico && (
                  <div><p className="text-xs font-semibold text-primary mb-1">Diagnóstico Técnico</p><p className="text-muted-foreground">{nar.diagnostico}</p></div>
                )}
                {[["Possíveis Causas", nar.causas], ["Ações Corretivas", nar.acoes_corretivas],
                  ["Ações Preventivas", nar.acoes_preventivas], ["Recomendações Técnicas", nar.recomendacoes],
                  ["Riscos", nar.riscos]].map(([label, items]) => (items || []).length > 0 && (
                  <div key={label}>
                    <p className="text-xs font-semibold text-primary mb-1">{label}</p>
                    <ul className="list-disc list-inside space-y-0.5 text-muted-foreground">
                      {items.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  </div>
                ))}
                {nar.frequencia_monitoramento && (
                  <div><p className="text-xs font-semibold text-primary mb-1">Frequência de Monitoramento Recomendada</p><p className="text-muted-foreground">{nar.frequencia_monitoramento}</p></div>
                )}
                <p className="text-[11px] text-muted-foreground pt-2 border-t border-border">
                  Gerada por {nar.model} em {formatDate(nar.generated_at)}{nar.edited_by ? ` · revisada por ${nar.edited_by}` : ""}. O supervisor pode complementar o plano antes da aprovação.
                </p>
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <ActionDialog
        open={actionDialog}
        onOpenChange={setActionDialog}
        planId={id}
        initial={editingAction}
        canCreate={canCreate}
        canEdit={canEdit}
        onSaved={load}
      />
      {canReview && (
        <ReviewDialog
          open={reviewOpen}
          onOpenChange={setReviewOpen}
          entity="plano de ação"
          entityLabel={plan.number}
          url={`/action-plans/${id}/review`}
          onDone={load}
        />
      )}
      {canDelete && (
        <ConfirmDeleteDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          entity="plano de ação"
          entityLabel={`${plan.number} — ${plan.title}`}
          impactUrl={`/action-plans/${id}/delete-impact`}
          deleteUrl={`/action-plans/${id}`}
          onDeleted={() => navigate("/planos-de-acao")}
        />
      )}
    </div>
  );
}
